/* eslint-disable no-shadow,no-await-in-loop,no-restricted-syntax */

import { Action, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import _ from 'lodash';
import moment from 'moment';
import { v4 } from 'uuid';
import toast, { superAdminToast } from '../../utils/toast';

import { IAppState } from '../reducers';
import { selectUserOrgIds, selectManualDownloadPriority, selectIsUserVaultSuperAdmin } from '../../state/user/userOrgRole';
import { searchExchangeAssets, searchGames, searchGamesByTeamIds } from '../../api/vault/searchEngine/api';
import { IExchangeAssetHit } from '../../api/vault/searchEngine/interfaces';
import { createAssetStatus } from '../../api/vault/assetStatus/api';
import { getCompletedDownloads, getCompletedPushToClouds } from '../../api/vault/logs';
import { ActionStatus, ActionType as AssetActionType } from '../../api/vault/assetStatus/interfaces';
import {
  ISelectedTeam,
  DownloadFilter,
  selectSearchParams,
  selectSelectedTeams,
  selectAllTeams,
  addFilterToSearchParams,
  SeasonMap,
  PushToCloudFilter,
} from './searchParams';
import { resetActiveAsset } from './activeAsset';
import { VAULT_MEDIA_TYPE } from '../../constants';
import { fetchContentPreferences } from './contentPreferences';
import { getDownloadLink } from '../../api/vault/assetDetails';
import { Search } from '../../constants/search';

export interface ISelectableExchangeAssetHit extends IExchangeAssetHit {
  isSelected: boolean;
  isDownloaded: boolean;
  isPushedToCloud: boolean;
  isHiddenByQualifierFilter: boolean;
  isHiddenByFocusFilter: boolean;
  isHiddenByDownloadFilter: boolean;
  isHiddenBySeasonFilter: boolean;
  isHiddenByFileTypeFilter: boolean;
  sourceTeamImage: string,
}

export interface IExchangeGame {
  key: string;
  assets: ISelectableExchangeAssetHit[];
  homeTeamImage: string,
  awayTeamImage: string,
  isExpanded: boolean;
  isAllAssetsSelected: boolean;
  isAllAssetsDownloaded: boolean;
  isAllAssetsHidden: boolean;
  gameDate: string;
}

interface ITeamGames {
  name: string;
  total: number;
  hits: IExchangeAssetHit[],
}

interface IFetchedGames {
  games: IExchangeGame[];
  completedDownloads: string[];
  completedPushToClouds: string[];
  teamsWithoutGames: string[];
}

interface ITeamNameAndId {
  teamName: string;
  teamId: string;
}

export interface IState {
  games: IExchangeGame[];
  fileTypes: Array<string>;
  backgroundTask: number;
  completedDownloads: Array<string>;
  completedPushToClouds: Array<string>;
  teamsWithoutGames: string[];
  isLoading: boolean;
  isBackgroundTask: boolean;
  isAllGamesSelected: boolean;
  isAllGamesExpanded: boolean;
  notFoundOpen: boolean;
  exchangeSearchId: string;
}

export enum ActionType {
  UpdateGames = 'ExchangeSearchResults.UpdateGames',
  UpdateGamesProcedural = 'ExchangeSearchResults.UpdateGamesProcedural',
  UpdateDownloads = 'ExchangeSearchResults.UpdateDownloads',
  StartBackgroundTask = 'ExchangeSearchResults.StartBackgroundTask',
  StopBackgroundTask = 'ExchangeSearchResults.StopBackgroundTask',
  StartLoading = 'ExchangeSearchResults.StartLoading',
  FinishLoading = 'ExchangeSearchResults.FinishLoading',
  ToggleGameExpandedState = 'ExchangeSearchResults.ToggleGameExpandedState',
  ToggleAllGamesExpandedState = 'ExchangeSearchResults.ToggleAllGameExpandedState',
  ToggleGameSelection = 'ExchangeSearchResults.ToggleGameSelection',
  ToggleAssetSelection = 'ExchangeSearchResults.ToggleAssetSelection',
  ToggleAllGamesSelection = 'ExchangeSearchResults.ToggleAllGamesSelection',
  UpdateAssetsOnFilterByQualifier = 'ExchangeSearchResults.UpdateAssetsOnFilterByQualifier',
  UpdateAssetsOnFilterByFocus = 'ExchangeSearchResults.UpdateAssetsOnFilterByFocus',
  UpdateAssetsOnFilterByDownload = 'ExchangeSearchResults.UpdateAssetsOnFilterByDownload',
  UpdateAssetsOnFilterBySeason = 'ExchangeSearchResults.UpdateAssetsOnFilterBySeason',
  ToggleNotFound = 'ExchangeSearchResults.ToggleNotFound',
  UpdateAssetsOnFilterByFileType = 'ExchangeSearchResults.UpdateAssetsOnFilterByFileType',
  UpdateExchangeSearchId = 'ExchangeSearchResults.UpdateExchangeSearchId',
  UpdateAssetSource = 'ExchangeSearchResults.UpdateAssetSource',
  InitialState = 'ExchangeSearchResults.InitialState'
}

export interface IExchangeSearchResultsAction extends Action<ActionType> {
  payload?: any,
}

type IExchangeSearchResultsThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  IAppState,
  unknown,
  IExchangeSearchResultsAction
>

export const EXCHANGE_DOWNLOAD_UPDATE_STORAGE_KEY = 'catapult:exchangeDownloadUpdate';

const INITIAL_STATE: IState = {
  games: null,
  fileTypes: [],
  backgroundTask: -1,
  completedDownloads: [],
  completedPushToClouds: [],
  teamsWithoutGames: null,
  isLoading: false,
  isBackgroundTask: (() => { try { const s = localStorage.getItem(EXCHANGE_DOWNLOAD_UPDATE_STORAGE_KEY); return s && s !== 'undefined' ? !!JSON.parse(s) : false; } catch { return false; } })(),
  isAllGamesSelected: false,
  isAllGamesExpanded: false,
  notFoundOpen: false,
  exchangeSearchId: null,
};

export const isAssetAvailable = ({
  isHiddenByQualifierFilter,
  isHiddenByFocusFilter,
  isHiddenByDownloadFilter,
  isHiddenBySeasonFilter,
  isHiddenByFileTypeFilter,
}: ISelectableExchangeAssetHit): boolean =>
  // eslint-disable-next-line
  !isHiddenByQualifierFilter
  && !isHiddenByFocusFilter
  && !isHiddenByDownloadFilter
  && !isHiddenBySeasonFilter
  && !isHiddenByFileTypeFilter;

export const isGameAvailable = ({ assets }: IExchangeGame): boolean =>
  // eslint-disable-next-line
  assets.some(isAssetAvailable);

const isAssetSelected = (asset: ISelectableExchangeAssetHit): boolean =>
  // eslint-disable-next-line
  isAssetAvailable(asset) && asset.isSelected;

const isHiddenByQualifier = (
  isFilterByQualifier: boolean,
  qualifierFilter: Array<string>,
  asset: IExchangeAssetHit,
): boolean => {
  if (!isFilterByQualifier || qualifierFilter.length === 0) {
    return false;
  }
  const { _source: { exchangeQualifier } } = asset;
  return !qualifierFilter.includes(exchangeQualifier);
};

const isHiddenByFocus = (
  selectedTeams: any,
  asset: IExchangeAssetHit,
): boolean => {
  const focusedTeamIds = selectedTeams
    // @ts-ignore
    .filter(({ isFocused }) => isFocused)
    .map((team: any) => team.teamId);
  const { _source: { sourceTeamId } } = asset;
  return focusedTeamIds.length && !focusedTeamIds.includes(sourceTeamId);
};

const isHiddenByDownload = (
  isFilterByDownload: boolean,
  downloadFilter: DownloadFilter,
  asset: ISelectableExchangeAssetHit,
) => {
  if (!isFilterByDownload || !downloadFilter) {
    return false;
  }
  const { isDownloaded } = asset;
  return (downloadFilter === DownloadFilter.Downloaded && !isDownloaded) || (downloadFilter === DownloadFilter.NotDownloaded && isDownloaded);
};

const isHiddenByPushToCloud = (
  isFilterByPushToCloud: boolean,
  pushToCloudFilter: PushToCloudFilter,
  asset: ISelectableExchangeAssetHit,
) => {
  if (!isFilterByPushToCloud || !pushToCloudFilter) {
    return false;
  }
  const { isPushedToCloud, isDownloaded, _source: { type } } = asset;
  if (type !== VAULT_MEDIA_TYPE.video) {
    if ((isDownloaded && pushToCloudFilter === PushToCloudFilter.NotPushedToCloud)
      || (!isDownloaded && pushToCloudFilter === PushToCloudFilter.PushedToCloud)) {
      return true;
    }
  } else if ((isPushedToCloud && pushToCloudFilter === PushToCloudFilter.NotPushedToCloud)
    || (!isPushedToCloud && pushToCloudFilter === PushToCloudFilter.PushedToCloud)) {
    return true;
  }
  return false;
};

const isHiddenBySeason = (
  isFilterBySeason: boolean,
  seasonFilter: string,
  asset: ISelectableExchangeAssetHit,
  seasonMap: SeasonMap,
) => {
  if (!isFilterBySeason || !seasonFilter) {
    return false;
  }
  const { _source: { exchangeScheduleId } } = asset;
  if (!exchangeScheduleId) {
    // hide if asset does not have an associated scheduleId
    return true;
  }
  const season = seasonMap !== null ? seasonMap[exchangeScheduleId] : null;
  if (!season) {
    // hide if season not found in our mapping
    return true;
  }
  // hide if asset season does not match season filter
  return season !== seasonFilter;
};

const isHiddenByFileType = (
  isFilterByFileType: boolean,
  fileTypeFilter: Array<string>,
  asset: IExchangeAssetHit,
): boolean => {
  if (!isFilterByFileType || fileTypeFilter.length === 0) {
    return false;
  }
  const { _source: { type: fileType } } = asset;
  return !fileTypeFilter.includes(fileType);
};

function handleGameSelectionToggle(state: IState, action: IExchangeSearchResultsAction): IState {
  const games = (state.games || []).reduce((updatedGames, game) => {
    if (game.key === action.payload.gameKey) {
      const assets = game.assets.map((asset) => ({
        ...asset,
        isSelected: !game.isAllAssetsSelected,
      }));
      updatedGames.push({
        ...game,
        assets,
        isAllAssetsSelected: !game.isAllAssetsSelected,
      });
    } else {
      updatedGames.push(game);
    }
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function handleGameExpandedStateToggle(state: IState, action: IExchangeSearchResultsAction): IState {
  const games = (state.games || []).reduce((updatedGames, game) => {
    if (game.key === action.payload.gameKey) {
      updatedGames.push({
        ...game,
        isExpanded: !game.isExpanded,
      });
    } else {
      updatedGames.push(game);
    }
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesExpanded: games.filter(isGameAvailable).every(({ isExpanded }) => isExpanded),
  };
}

/**
 * Merge freshly saved asset fields into a hit already in the results.
 *
 * The results list is served from OpenSearch, which is not searchable until its refresh
 * interval elapses (a second by default). A re-query straight after a save can therefore come
 * back with the pre-save document, so the list keeps showing stale values until the next
 * search. Patching the hit from the API's own response is immediate and cannot lose that race.
 *
 * Merged rather than replaced: the hit's _source is the indexed subset and carries fields the
 * update response does not, so overwriting it would drop them.
 */
function handleAssetSourceUpdate(state: IState, action: IExchangeSearchResultsAction): IState {
  const { gameKey, assetId, source } = action.payload;
  return {
    ...state,
    games: (state.games || []).map((game) => (game.key !== gameKey ? game : {
      ...game,
      assets: game.assets.map((asset) => (asset._id !== assetId ? asset : {
        ...asset,
        _source: { ...asset._source, ...source },
      })),
    })),
  };
}

function handleAssetSelectionToggle(state: IState, action: IExchangeSearchResultsAction): IState {
  const games = (state.games || []).reduce((updatedGames, game) => {
    if (game.key === action.payload.gameKey) {
      const assets = game.assets.map((asset) => {
        if (asset._id === action.payload.assetId) {
          return {
            ...asset,
            isSelected: !asset.isSelected,
          };
        }
        return asset;
      });
      updatedGames.push({
        ...game,
        assets,
        isAllAssetsSelected: assets.every(isAssetSelected),
      });
    } else {
      updatedGames.push(game);
    }
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function handleAllGamesSelectionToggle(state: IState, action: IExchangeSearchResultsAction): IState {
  return {
    ...state,
    isAllGamesSelected: !state.isAllGamesSelected,
    games: (state.games || []).reduce((games, game) => {
      const assets = game.assets.map((asset) => ({
        ...asset,
        isSelected: !state.isAllGamesSelected,
      }));
      games.push({
        ...game,
        assets,
        isAllAssetsSelected: !state.isAllGamesSelected,
      });
      return games;
    }, []),
  };
}

function updateAssetsOnFilterByQualifier(state: IState, action: IExchangeSearchResultsAction): IState {
  const {
    isFilterByQualifier,
    qualifier,
  } = action.payload;

  if (state.games === null) {
    return state;
  }

  const games = state.games.reduce((updatedGames, game) => {
    const assets = game.assets.map((asset) => {
      const isHiddenByQualifierFilter = isHiddenByQualifier(isFilterByQualifier, qualifier, asset);
      return {
        ...asset,
        isHiddenByQualifierFilter,
      };
    });
    updatedGames.push({
      ...game,
      assets,
      isAllAssetsSelected: assets.filter(isAssetAvailable).every(isAssetSelected),
      isAllAssetsHidden: !assets.some(isAssetAvailable),
    });
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function updateAssetsOnFilterByFocus(state: IState, action: IExchangeSearchResultsAction): IState {
  const { selectedTeams } = action.payload;

  if (state.games === null) {
    return state;
  }
  const gamesDeepCopy = [...state.games];
  const games = gamesDeepCopy.reduce((updatedGames, game) => {
    const assets = game.assets.map((asset) => {
      const isHiddenByFocusFilter = isHiddenByFocus(selectedTeams, asset);
      return {
        ...asset,
        isHiddenByFocusFilter,
      };
    });
    updatedGames.push({
      ...game,
      assets,
      isAllAssetsSelected: assets.filter(isAssetAvailable).every(isAssetSelected),
      isAllAssetsHidden: !assets.some(isAssetAvailable),
    });
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function updateAssetsOnFilterByDownload(state: IState, action: IExchangeSearchResultsAction): IState {
  const {
    downloadFilter,
    isFilterByDownload,
    isPushToCloud,
  } = action.payload;

  if (state.games === null) {
    return state;
  }

  const games = state.games.reduce((updatedGames, game) => {
    const assets = game.assets.map((asset) => {
      let isHiddenByDownloadFilter: boolean;
      if (isPushToCloud) {
        isHiddenByDownloadFilter = isHiddenByPushToCloud(isFilterByDownload, downloadFilter, asset);
      } else {
        isHiddenByDownloadFilter = isHiddenByDownload(isFilterByDownload, downloadFilter, asset);
      }
      return {
        ...asset,
        isHiddenByDownloadFilter,
      };
    });
    updatedGames.push({
      ...game,
      assets,
      isAllAssetsSelected: assets.filter(isAssetAvailable).every(isAssetSelected),
      isAllAssetsHidden: !assets.some(isAssetAvailable),
    });
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function updateAssetsOnFilterBySeason(state: IState, action: IExchangeSearchResultsAction): IState {
  const {
    seasonFilter,
    isFilterBySeason,
    seasonMap,
  } = action.payload;

  if (state.games === null) {
    return state;
  }

  const games = state.games.reduce((updatedGames, game) => {
    const assets = game.assets.map((asset) => {
      const isHiddenBySeasonFilter = isHiddenBySeason(isFilterBySeason, seasonFilter, asset, seasonMap);
      return {
        ...asset,
        isHiddenBySeasonFilter,
      };
    });
    updatedGames.push({
      ...game,
      assets,
      isAllAssetsSelected: assets.filter(isAssetAvailable).every(isAssetSelected),
      isAllAssetsHidden: !assets.some(isAssetAvailable),
    });
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function updateAssetsOnFilterByFileType(state: IState, action: IExchangeSearchResultsAction): IState {
  const {
    isFilterByFileType,
    fileTypeFilter,
  } = action.payload;

  if (state.games === null) {
    return state;
  }

  const games = state.games.reduce((updatedGames, game) => {
    const assets = game.assets.map((asset) => {
      const isHiddenByFileTypeFilter = isHiddenByFileType(isFilterByFileType, fileTypeFilter, asset);
      return {
        ...asset,
        isHiddenByFileTypeFilter,
      };
    });
    updatedGames.push({
      ...game,
      assets,
      isAllAssetsSelected: assets.filter(isAssetAvailable).every(isAssetSelected),
      isAllAssetsHidden: !assets.some(isAssetAvailable),
    });
    return updatedGames;
  }, []);

  return {
    ...state,
    games,
    isAllGamesSelected: games.every(({ isAllAssetsSelected }) => isAllAssetsSelected),
  };
}

function updateExchangeSearchId(state: IState, action: IExchangeSearchResultsAction): IState {
  const {
    exchangeSearchId,
  } = action.payload;
  return {
    ...state,
    exchangeSearchId,
  };
}

export default function reducer(state: IState = INITIAL_STATE, action: IExchangeSearchResultsAction): IState {
  switch (action.type) {
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
      };
    case ActionType.UpdateGames:
      return {
        ...state,
        fileTypes: action.payload.fileTypes,
        games: action.payload.games,
        completedDownloads: action.payload.completedDownloads,
        completedPushToClouds: action.payload.completedPushToClouds,
        teamsWithoutGames: action.payload.teamsWithoutGames,
        isAllGamesSelected: INITIAL_STATE.isAllGamesSelected,
        isAllGamesExpanded: INITIAL_STATE.isAllGamesExpanded,
      };
    case ActionType.UpdateGamesProcedural: {
      return {
        ...state,
        games: action.payload.games.reduce((games: Array<IExchangeGame>, game: IExchangeGame, index: number) => {
          const match = (state.games || []).find(({ key }) => key === game.key);
          if (match) {
            return [...games, {
              // note: retain game properties such as isExpanded
              ...match,
              // note: retain filters...
              assets: game.assets.reduce((assets: Array<ISelectableExchangeAssetHit>, asset: ISelectableExchangeAssetHit) => {
                const matchingAsset = match.assets.find(({ _id }) => _id === asset._id);
                if (matchingAsset) {
                  return [...assets, matchingAsset];
                }
                return [...assets, asset];
              }, [] as Array<ISelectableExchangeAssetHit>),
            }];
          }
          return [...games, game];
        }, [] as Array<IExchangeGame>),
      };
    }
    case ActionType.UpdateDownloads:
      return {
        ...state,
        games: action.payload.games,
        completedDownloads: action.payload.completedDownloads,
        completedPushToClouds: action.payload.completedPushToClouds,
      };
    case ActionType.StartBackgroundTask:
      localStorage.setItem(EXCHANGE_DOWNLOAD_UPDATE_STORAGE_KEY, 'true');
      return {
        ...state,
        backgroundTask: action.payload.backgroundTask,
        isBackgroundTask: true,
      };
    case ActionType.StopBackgroundTask:
      localStorage.setItem(EXCHANGE_DOWNLOAD_UPDATE_STORAGE_KEY, 'false');
      return {
        ...state,
        backgroundTask: INITIAL_STATE.backgroundTask,
        isBackgroundTask: false,
      };
    case ActionType.ToggleGameExpandedState:
      return handleGameExpandedStateToggle(state, action);
    case ActionType.ToggleAllGamesExpandedState:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.ToggleGameSelection:
      return handleGameSelectionToggle(state, action);
    case ActionType.ToggleAssetSelection:
      return handleAssetSelectionToggle(state, action);
    case ActionType.ToggleAllGamesSelection:
      return handleAllGamesSelectionToggle(state, action);
    case ActionType.UpdateAssetsOnFilterByQualifier:
      return updateAssetsOnFilterByQualifier(state, action);
    case ActionType.UpdateAssetsOnFilterByFocus:
      return updateAssetsOnFilterByFocus(state, action);
    case ActionType.UpdateAssetsOnFilterByDownload:
      return updateAssetsOnFilterByDownload(state, action);
    case ActionType.UpdateAssetsOnFilterBySeason:
      return updateAssetsOnFilterBySeason(state, action);
    case ActionType.ToggleNotFound:
      return {
        ...state,
        notFoundOpen: !state.notFoundOpen,
      };
    case ActionType.UpdateAssetsOnFilterByFileType:
      return updateAssetsOnFilterByFileType(state, action);
    case ActionType.UpdateExchangeSearchId:
      return updateExchangeSearchId(state, action);
    case ActionType.UpdateAssetSource:
      return handleAssetSourceUpdate(state, action);
    case ActionType.InitialState:
      return {
        ...INITIAL_STATE,
      };
    default:
      return state;
  }
}

function getExchangeSearchId(state: IAppState): string {
  return state.exchange.searchResults.exchangeSearchId;
}

async function fetchGamesForTeams(state: IAppState, selectedTeams: ITeamNameAndId[], page: number = Search.DEFAULT_PAGE): Promise<ITeamGames> {
  const searchParams = { ...selectSearchParams(state), size: Search.DEFAULT_EXCHANGE_RESULTS_LIMIT_SIZE_LARGE };
  const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);

  const teamIds: string[] = selectedTeams.map(({ teamId }) => teamId);

  try {
    const response = await searchGamesByTeamIds({ ...searchParams, page }, teamIds);

    return {
      name: 'Multiple Teams Games Search',
      total: response.data.data.hits.total.value,
      hits: response.data.data.hits.hits,
    };
  } catch (e) {
    superAdminToast(isVaultSuperAdmin, `Error fetching games for "${selectedTeams.length} teams"`, { type: toast.TYPE.ERROR });
    return {
      name: 'Multiple Teams Games Search',
      total: 0,
      hits: [],
    };
  }
}

function groupAssetsByGame(state: IAppState, allTeams: ITeamGames[]) {
  const {
    exchange: {
      searchParams: {
        teamImageMap,
        isFilterByQualifier,
        qualifierFilter,
        isFilterByDownload,
        downloadFilter,
        isFilterBySeason,
        seasonFilter,
        seasonMap,
        isFilterByFileType,
        fileTypeFilter,
      },
      searchResults: { completedDownloads, completedPushToClouds },
      contentPreferences: { subscription: { isPushToCloud } },
    },
  } = state;

  const selectedTeams = selectSelectedTeams(state);
  const games: IExchangeGame[] = [];

  allTeams.forEach(({ hits }) => {
    hits.forEach((hit) => {
      const {
        exchangeGameKey,
        homeTeamId,
        awayTeamId,
        sourceTeamId,
        gameDate,
      } = hit._source;
      const game = games.find(({ key }) => key === exchangeGameKey);
      const isDownloaded = completedDownloads.includes(hit._id);
      const isPushedToCloud = completedPushToClouds.includes(hit._id);
      const newAsset = {
        ...hit,
        isSelected: false,
        isDownloaded,
        isPushedToCloud,
        isHiddenByQualifierFilter: isHiddenByQualifier(isFilterByQualifier, qualifierFilter, hit),
        isHiddenByFocusFilter: isHiddenByFocus(selectedTeams, hit),
        isHiddenByDownloadFilter: isHiddenByDownload(isFilterByDownload, downloadFilter, { isDownloaded } as ISelectableExchangeAssetHit),
        isHiddenBySeasonFilter: isHiddenBySeason(isFilterBySeason, seasonFilter, hit as ISelectableExchangeAssetHit, seasonMap),
        isHiddenByFileTypeFilter: isHiddenByFileType(isFilterByFileType, fileTypeFilter, hit as ISelectableExchangeAssetHit),
        sourceTeamImage: teamImageMap[sourceTeamId],
      };

      if (game) {
        const isAssetAlreadyAdded = game.assets.some(({ _id }) => _id === hit._id);
        if (!isAssetAlreadyAdded) {
          game.assets.push(newAsset);
          game.isAllAssetsDownloaded = game.assets.every(({ isDownloaded }) => isDownloaded);
          game.isAllAssetsHidden = !game.assets.some(isAssetAvailable);
        }
      } else {
        games.push({
          key: exchangeGameKey,
          isExpanded: false,
          isAllAssetsSelected: false,
          isAllAssetsDownloaded: newAsset.isDownloaded,
          isAllAssetsHidden: !isAssetAvailable(newAsset),
          assets: [newAsset],
          homeTeamImage: teamImageMap[homeTeamId],
          awayTeamImage: teamImageMap[awayTeamId],
          gameDate: moment(new Date(gameDate)).format('YYYY-MM-DD'),
        });
      }
    });
  });

  return games;
}

function sortAssets(games: IExchangeGame[]) {
  return games.map((game: IExchangeGame) => ({
    ...game,
    assets: _.sortBy(game.assets, (asset: ISelectableExchangeAssetHit) => asset._source.title),
  }));
}

export function sortGames(games: IExchangeGame[]) {
  return _.sortBy(games, ({ gameDate }) => new Date(gameDate).getTime());
}

function markAssetsDownloaded(games: Array<IExchangeGame>, completedDownloads: Array<string>, completedPushToClouds: Array<string>) {
  return games.reduce((updatedGames, game) => {
    const assets = game.assets.map((asset) => ({
      ...asset,
      isDownloaded: completedDownloads.includes(asset._id),
      isPushedToCloud: completedPushToClouds.includes(asset._id),
    }));
    updatedGames.push({
      ...game,
      assets,
      isAllAssetsDownloaded: assets.every(({ isDownloaded }) => isDownloaded),
    });
    return updatedGames;
  }, []);
}

async function fetchDownloads(orgId: number) {
  let completedDownloads: Array<string> = [];
  try {
    const res = await getCompletedDownloads(orgId);
    completedDownloads = _.uniq(res.data.data.map(({ assetId }) => assetId));
  } catch (e) {
    console.error('[QA] Failed to fetch completed downloads\n', e.toString());
  }
  return completedDownloads;
}

async function fetchPushToClouds(orgId: number) {
  let completedPushToClouds: Array<string> = [];
  try {
    const res = await getCompletedPushToClouds(orgId);
    completedPushToClouds = _.uniq(res.data.data.map(({ assetId }) => assetId));
  } catch (e) {
    console.error('[QA] Failed to fetch completed push to clouds\n', e.toString());
  }
  return completedPushToClouds;
}

async function fetchGames(getState: () => IAppState, dispatch: Dispatch) {
  let state = getState();
  const [orgId] = selectUserOrgIds(state);
  const selectedTeams = selectSelectedTeams(state);
  const teams = selectedTeams.length === 0 ? selectAllTeams(state) : selectedTeams;

  const response: Array<ITeamGames> = [];
  const teamsWithoutGames: Array<string> = [];
  const searchId = v4();

  let totalHits: number = 0;

  dispatch({
    type: ActionType.UpdateExchangeSearchId,
    payload: {
      exchangeSearchId: searchId,
    },
  });

  dispatch({ type: ActionType.StartLoading });

  // the max clause count for elastic Search is 1024. Accounting for our other params, we can only fetch 20 teams at a time to keep under 10K results
  const fetchGamesChunks: ITeamNameAndId[][] = _.chunk(teams.map(({ teamName, teamId }) => ({ teamName, teamId })), 20);

  // each chunk must wait for the previous to complete
  for (const chunk of fetchGamesChunks) {
    if (totalHits >= Search.DEFAULT_DOWNLOADS_RESULTS_LIMIT_SIZE) {
      break;
    }

    let page = Search.DEFAULT_PAGE;
    let totalPages = 0;

    state = getState();

    const currentSearchId = getExchangeSearchId(state);

    if (searchId !== currentSearchId) {
      return null;
    }

    const fetchedGames = await fetchGamesForTeams(state, chunk, page);

    if (fetchedGames.total > 0 || fetchedGames.hits.length > 0) {
      totalHits += fetchedGames.hits.length;
      response.push(fetchedGames);
      dispatch({
        type: ActionType.UpdateGamesProcedural,
        payload: {
          games: sortGames(sortAssets(markAssetsDownloaded(groupAssetsByGame(state, response), [], []))),
        },
      });

      if (totalHits >= Search.DEFAULT_DOWNLOADS_RESULTS_LIMIT_SIZE) {
        superAdminToast(false, `This Search was limited to ${totalHits} total. Please limit your results using the Date or Team filters.`, { type: toast.TYPE.WARNING });
        break;
      }

      if (fetchedGames.total > 0 && fetchedGames.total > fetchedGames.hits.length) {
        totalPages = Math.ceil(fetchedGames.total / Search.DEFAULT_EXCHANGE_RESULTS_LIMIT_SIZE_LARGE);

        const pages: number[] = [];

        while (page < totalPages && pages.length < 9) {
          page += 1;
          pages.push(page);
        }

        // each pagenated response must wait for the previous to complete
        for (const page of pages) {
          state = getState();

          const currentSearchId = getExchangeSearchId(state);

          if (searchId !== currentSearchId) {
            return null;
          }

          const nextPageResponse = await fetchGamesForTeams(state, chunk, page);

          if (nextPageResponse.total > 0 || nextPageResponse.hits.length > 0) {
            totalHits += nextPageResponse.hits.length;
            response.push(nextPageResponse);
            dispatch({
              type: ActionType.UpdateGamesProcedural,
              payload: {
                games: sortGames(sortAssets(markAssetsDownloaded(groupAssetsByGame(state, response), [], []))),
              },
            });
            if (totalHits >= Search.DEFAULT_DOWNLOADS_RESULTS_LIMIT_SIZE) {
              superAdminToast(false, `This Search was limited to ${totalHits} total. Please limit your results using the Date or Team filters.`, { type: toast.TYPE.WARNING });
              break;
            }
          }
        }
      }
    }

    if (fetchedGames.total === 0) {
      chunk.forEach(({ teamName }) => { teamsWithoutGames.push(teamName); });
    }
  }

  dispatch({ type: ActionType.FinishLoading });

  state = getState();

  const games = groupAssetsByGame(state, response);

  const completedDownloads = await fetchDownloads(orgId);

  const completedPushToClouds = await fetchPushToClouds(orgId);

  return {
    games: sortGames(sortAssets(markAssetsDownloaded(games, completedDownloads, completedPushToClouds))),
    completedDownloads,
    completedPushToClouds,
    teamsWithoutGames,
  };
}

// TODO: remove timeout once VA-1136 addressed
export const queryGames = (timeout: number = 0) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  dispatch(resetActiveAsset());
  dispatch({
    type: ActionType.StartLoading,
  });

  setTimeout(async () => {
    const fetchedGames = await fetchGames(getState, dispatch);

    if (fetchedGames) {
      const {
        games,
        completedDownloads,
        completedPushToClouds,
        teamsWithoutGames,
      } = fetchedGames;

      const fileTypes = [...games
        .reduce((assets, game) => [...assets, ...game.assets], [])
        .reduce((types, { _source: { type: fileType } }) => {
          types.add(fileType);
          return types;
        }, new Set())];

      fetchContentPreferences()(dispatch, getState);

      dispatch({
        type: ActionType.UpdateGames,
        payload: {
          fileTypes,
          games,
          completedDownloads,
          completedPushToClouds,
          teamsWithoutGames,
        },
      });
      dispatch({
        type: ActionType.FinishLoading,
      });
    }
  }, timeout);
};

export const updateDownloads = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const [orgId] = selectUserOrgIds(state);
  const {
    exchange: {
      searchResults: {
        games,
        isLoading,
      },
    },
  } = state;
  if (games && games.length && !isLoading) {
    const completedDownloads = await fetchDownloads(orgId);
    const completedPushToClouds = await fetchPushToClouds(orgId);
    dispatch({
      type: ActionType.UpdateDownloads,
      payload: {
        completedDownloads,
        completedPushToClouds,
        games: markAssetsDownloaded(games, completedDownloads, completedPushToClouds),
      },
    });
  }
};

export const startBackgroundTask = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  let {
    exchange: {
      searchResults: {
        backgroundTask,
      },
    },
  } = state;
  if (backgroundTask === INITIAL_STATE.backgroundTask) {
    // note: update game downloads every 15 seconds
    const { setInterval } = window;
    backgroundTask = setInterval(() => {
      updateDownloads()(dispatch, getState);
    }, 15000);
    dispatch({
      type: ActionType.StartBackgroundTask,
      payload: {
        backgroundTask,
      },
    });
  }
};

export const stopBackgroundTask = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    exchange: {
      searchResults: {
        backgroundTask,
      },
    },
  } = state;
  if (backgroundTask !== -1) {
    const { clearInterval } = window;
    clearInterval(backgroundTask);
    dispatch({
      type: ActionType.StopBackgroundTask,
    });
  }
};

export const toggleBackgroundTask = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    exchange: {
      searchResults: {
        isBackgroundTask,
      },
    },
  } = state;

  if (isBackgroundTask) {
    stopBackgroundTask()(dispatch, getState);
  } else {
    startBackgroundTask()(dispatch, getState);
  }
};

export const toggleGameExpandedState = (gameKey: string) => ({
  type: ActionType.ToggleGameExpandedState,
  payload: {
    gameKey,
  },
});

const handleAllGamesExpandedStateToggle = (state: IState): IState => {
  const { isAllGamesExpanded } = state;
  return {
    ...state,
    isAllGamesExpanded: !isAllGamesExpanded,
    games: (state.games || []).reduce((games, game) => {
      games.push({
        ...game,
        isExpanded: !isAllGamesExpanded,
      });
      return games;
    }, []),
  };
};

export const toggleAllGamesExpandedState = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.StartLoading,
  });

  const { exchange: { searchResults } } = getState();
  // using timeout to display loading on nextTick
  setTimeout(() => {
    const payload = handleAllGamesExpandedStateToggle(searchResults);
    dispatch({
      type: ActionType.ToggleAllGamesExpandedState,
      payload,
    });

    dispatch({
      type: ActionType.FinishLoading,
    });
  }, 100);
};

export const toggleAssetSelection = (gameKey: string, assetId: string) => ({
  type: ActionType.ToggleAssetSelection,
  payload: {
    gameKey,
    assetId,
  },
});

export const updateAssetSource = (gameKey: string, assetId: string, source: object) => ({
  type: ActionType.UpdateAssetSource,
  payload: {
    gameKey,
    assetId,
    source,
  },
});

export const toggleGameSelection = (gameKey: string) => ({
  type: ActionType.ToggleGameSelection,
  payload: {
    gameKey,
  },
});

export const toggleAllGamesSelection = () => ({
  type: ActionType.ToggleAllGamesSelection,
});

export const selectActiveAssetDetails = (state: IAppState): ISelectableExchangeAssetHit => {
  const {
    gameKey,
    assetId,
  } = state.exchange.activeAsset;

  return state.exchange.searchResults.games?.find(({ key }) => key === gameKey)?.assets?.find(({ _id }) => _id === assetId);
};

export const queueAssetDownload = (gameKey: string, assetId: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    exchange: {
      contentPreferences: {
        subscription: {
          isPushToCloud,
        },
      },
    },
  } = state;
  const [orgId] = selectUserOrgIds(state);
  const asset = state.exchange.searchResults.games?.find(({ key }) => key === gameKey)?.assets?.find(({ _id }) => _id === assetId);
  const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);

  let prev = null;
  try {
    const preview = await getDownloadLink({ id: assetId }, 1, 'probe', false);
    prev = preview.data.data;
  } catch {
    prev = null;
  }

  if (!prev) {
    superAdminToast(false, `Could not add ${asset._source.title} to queue: Asset processing, try again later.`, { type: toast.TYPE.WARNING });
    return;
  }

  let assetActionType = AssetActionType.Download;
  if (isPushToCloud && asset._source.type === VAULT_MEDIA_TYPE.video) {
    assetActionType = AssetActionType.PushToCloud;
  }

  if (!asset._source.previewImageUrl || !asset._source.thumbnailImageUrl) {
    const reason = !asset._source.previewImageUrl ? 'Preview not available, try again later' : 'Transcode not complete, try again later.';
    superAdminToast(false, `Error adding "${asset._source.title}" to queue: ${reason} `, { type: toast.TYPE.ERROR });
  } else {
    const manualDownloadPriority = selectManualDownloadPriority(state);
    const actionPriority = manualDownloadPriority ? '1' : '3';
    const params = {
      assetName: asset._source.title,
      assetId: asset._id,
      actionType: assetActionType,
      actionStatus: ActionStatus.Queued,
      videoType: asset._source.videoType,
      orgId,
      actionPriority,
    };

    createAssetStatus(params);
  }
};

const toastConsoleNonVideoAsset = (asset: ISelectableExchangeAssetHit, isVaultSuperAdmin: boolean) => {
  superAdminToast(isVaultSuperAdmin, `Unable to push non-video asset "${asset._source.title}" to thundercloud. Adding to console queue download.`, { type: toast.TYPE.INFO });
};

export const queueGameDownload = (gameKey: string): IExchangeSearchResultsThunk => async (
  dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    exchange: { contentPreferences: { subscription: { isPushToCloud } } },
  } = state;
  const game = state.exchange.searchResults.games?.find(({ key }) => key === gameKey);
  const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);

  const nonVideoAssets: ISelectableExchangeAssetHit[] = [];
  let totalAssets = 0;
  game?.assets?.forEach((asset) => {
    if (isAssetAvailable(asset)) {
      dispatch(queueAssetDownload(gameKey, asset._id));
      // Check if mix of video and non-video assets
      if (isPushToCloud) {
        totalAssets += 1;
        if (asset._source.type !== VAULT_MEDIA_TYPE.video) {
          nonVideoAssets.push(asset);
        }
      }
    }
  });
  // Notify with toast because mix of video and non-video assets
  if (isPushToCloud && nonVideoAssets.length !== 0 && nonVideoAssets.length !== totalAssets) {
    nonVideoAssets.forEach((asset) => {
      if (isAssetAvailable(asset)) {
        toastConsoleNonVideoAsset(asset, isVaultSuperAdmin);
      }
    });
  }
};

export function selectSelectedAssets(state: IAppState) {
  if (!state.exchange.searchResults.games) {
    return [];
  }
  return state.exchange.searchResults.games.reduce((selectedAssets, game) => {
    game.assets.filter(isAssetSelected).forEach(({ _id, _source }) => {
      selectedAssets.push({ gameKey: game.key, assetId: _id, type: _source.type });
    });
    return selectedAssets;
  }, []);
}

export const queueSelectedAssets = (): IExchangeSearchResultsThunk => async (
  dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const selectedAssets = selectSelectedAssets(state);
  const {
    exchange: { contentPreferences: { subscription: { isPushToCloud } } },
  } = state;
  const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);
  const nonVideoAssets: ISelectableExchangeAssetHit[] = [];
  selectedAssets.forEach(({ gameKey, assetId }) => {
    dispatch(queueAssetDownload(gameKey, assetId));
    // Check if mix of video and non-video assets
    if (isPushToCloud) {
      const asset = state.exchange.searchResults.games?.find(({ key }) => key === gameKey)?.assets?.find(({ _id }) => _id === assetId);
      if (asset?._source?.type !== VAULT_MEDIA_TYPE.video) {
        nonVideoAssets.push(asset);
      }
    }
  });
  // Notify with toast because mix of video and non-video assets
  if (isPushToCloud && nonVideoAssets.length !== 0 && nonVideoAssets.length !== selectedAssets.length) {
    nonVideoAssets.forEach((asset) => {
      if (isAssetAvailable(asset)) {
        toastConsoleNonVideoAsset(asset, isVaultSuperAdmin);
      }
    });
  }
};

export function selectIsAssetsSelected(state: IAppState) {
  if (state.exchange.searchResults.games === null) {
    return false;
  }
  return selectSelectedAssets(state).length > 0;
}

export const toggleNotFound = () => ({
  type: ActionType.ToggleNotFound,
});

export const initialState = () => ({
  type: ActionType.InitialState,
});
